// Settings View - Comprehensive application settings
import React, { useState, useCallback } from 'react';
import {
  Settings,
  Monitor,
  Database,
  RefreshCw,
  Globe,
  Bell,
  Download,
  Info,
  Check,
  RotateCcw,
  Upload,
  Trash2,
  Copy,
  ExternalLink,
} from 'lucide-react';

// Settings interface
interface AppSettings {
  theme: 'dark' | 'terminal-green' | 'terminal-amber';
  compactMode: boolean;
  animationSpeed: number;
  defaultCountry: string;
  defaultTimeRange: '5y' | '10y' | '15y' | '20y' | 'all';
  autoRefresh: boolean;
  refreshInterval: number;
  enableIMF: boolean;
  enableWorldBank: boolean;
  enableStaticFallback: boolean;
  enableNotifications: boolean;
  alertThreshold: number;
  defaultExportFormat: 'csv' | 'json' | 'xlsx';
}

// Default settings
const DEFAULT_SETTINGS: AppSettings = {
  theme: 'dark',
  compactMode: false,
  animationSpeed: 2,
  defaultCountry: 'UZB',
  defaultTimeRange: '10y',
  autoRefresh: true,
  refreshInterval: 5,
  enableIMF: true,
  enableWorldBank: true,
  enableStaticFallback: true,
  enableNotifications: true,
  alertThreshold: 5,
  defaultExportFormat: 'csv',
};

const STORAGE_KEY = 'silk-road-terminal-settings';

// Load settings from localStorage
const loadSettings = (): AppSettings => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.error('Failed to load settings:', e);
  }
  return DEFAULT_SETTINGS;
};

// Save settings to localStorage
const saveSettings = (settings: AppSettings): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (e) {
    console.error('Failed to save settings:', e);
  }
};

// Country options
const COUNTRY_OPTIONS = [
  { code: 'UZB', name: 'Uzbekistan' },
  { code: 'KAZ', name: 'Kazakhstan' },
  { code: 'KGZ', name: 'Kyrgyzstan' },
  { code: 'TJK', name: 'Tajikistan' },
  { code: 'TKM', name: 'Turkmenistan' },
  { code: 'AZE', name: 'Azerbaijan' },
  { code: 'GEO', name: 'Georgia' },
  { code: 'MNG', name: 'Mongolia' },
];

// Time range options
const TIME_RANGE_OPTIONS = [
  { value: '5y', label: '5 Years' },
  { value: '10y', label: '10 Years' },
  { value: '15y', label: '15 Years' },
  { value: '20y', label: '20 Years' },
  { value: 'all', label: 'All Available' },
];

// Theme options
const THEME_OPTIONS = [
  { value: 'dark', label: 'Dark' },
  { value: 'terminal-green', label: 'Terminal Green' },
  { value: 'terminal-amber', label: 'Terminal Amber' },
];

// Refresh interval options
const REFRESH_INTERVAL_OPTIONS = [
  { value: 1, label: '1 minute' },
  { value: 5, label: '5 minutes' },
  { value: 10, label: '10 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
];

// Export format options
const EXPORT_FORMAT_OPTIONS = [
  { value: 'csv', label: 'CSV' },
  { value: 'json', label: 'JSON' },
  { value: 'xlsx', label: 'Excel' },
];

// Toggle Switch Component
const ToggleSwitch: React.FC<{
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  label: string;
  description?: string;
}> = ({ enabled, onChange, label, description }) => (
  <div className="flex items-center justify-between py-3">
    <div>
      <span className="text-sm text-white">{label}</span>
      {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
    </div>
    <button
      onClick={() => onChange(!enabled)}
      className={`relative w-11 h-6 rounded-full transition-colors ${
        enabled ? 'bg-data-green' : 'bg-gray-600'
      }`}
    >
      <span
        className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  </div>
);

// Select Component
const SelectOption: React.FC<{
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  label: string;
}> = ({ value, options, onChange, label }) => (
  <div className="flex items-center justify-between py-3">
    <span className="text-sm text-white">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-terminal-bg border border-terminal-border rounded px-3 py-1.5 text-sm text-gray-300 focus:outline-none focus:border-data-amber/50"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

// Section Header Component
const SectionHeader: React.FC<{
  icon: React.ElementType;
  title: string;
  description?: string;
}> = ({ icon: Icon, title, description }) => (
  <div className="flex items-center gap-3 pb-3 border-b border-terminal-border mb-3">
    <div className="w-8 h-8 rounded bg-data-amber/10 flex items-center justify-center">
      <Icon size={16} className="text-data-amber" />
    </div>
    <div>
      <h3 className="text-sm font-semibold text-white">{title}</h3>
      {description && <p className="text-[10px] text-gray-500">{description}</p>}
    </div>
  </div>
);

// Settings Card Component
const SettingsCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-terminal-panel border border-terminal-border rounded-lg p-4">
    {children}
  </div>
);

// Main Settings View
const SettingsView: React.FC = () => {
  const [settings, setSettings] = useState<AppSettings>(() => loadSettings());
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showClearCacheConfirm, setShowClearCacheConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Update a single setting
  const updateSetting = useCallback(<K extends keyof AppSettings>(key: K, value: AppSettings[K]) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      saveSettings(newSettings);
      return newSettings;
    });
  }, []);

  // Reset to defaults
  const handleReset = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
    saveSettings(DEFAULT_SETTINGS);
    setShowResetConfirm(false);
    setSuccessMessage('Settings reset to defaults');
    setTimeout(() => setSuccessMessage(null), 3000);
  }, []);

  // Clear cache
  const handleClearCache = useCallback(() => {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('wb-cache-') || key.startsWith('imf-cache-') || key.startsWith('macro-data-'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    setShowClearCacheConfirm(false);
    setSuccessMessage(`Cleared ${keysToRemove.length} cached items`);
    setTimeout(() => setSuccessMessage(null), 3000);
  }, []);

  // Export settings
  const handleExport = useCallback(() => {
    const json = JSON.stringify(settings, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'silk-road-settings.json';
    a.click();
    URL.revokeObjectURL(url);
    setSuccessMessage('Settings exported');
    setTimeout(() => setSuccessMessage(null), 3000);
  }, [settings]);

  // Import settings
  const handleImport = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const parsed = JSON.parse(event.target?.result as string);
            const newSettings = { ...DEFAULT_SETTINGS, ...parsed };
            setSettings(newSettings);
            saveSettings(newSettings);
            setSuccessMessage('Settings imported');
            setTimeout(() => setSuccessMessage(null), 3000);
          } catch {
            setSuccessMessage('Invalid settings file');
            setTimeout(() => setSuccessMessage(null), 3000);
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }, []);

  // Copy settings to clipboard
  const handleCopySettings = useCallback(() => {
    navigator.clipboard.writeText(JSON.stringify(settings, null, 2));
    setSuccessMessage('Settings copied to clipboard');
    setTimeout(() => setSuccessMessage(null), 3000);
  }, [settings]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-terminal-bg">
      {/* Header */}
      <div className="h-11 bg-terminal-header border-b border-terminal-border flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-2">
          <Settings size={16} className="text-data-amber" />
          <span className="text-data-amber font-bold text-sm tracking-wide">SETTINGS</span>
        </div>
        {successMessage && (
          <span className="text-xs text-data-green flex items-center gap-1">
            <Check size={12} />
            {successMessage}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-4xl mx-auto space-y-4">
          {/* Row 1: Display + Data Preferences */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Display Preferences */}
            <SettingsCard>
              <SectionHeader icon={Monitor} title="Display Preferences" description="Customize appearance" />
              <SelectOption
                value={settings.theme}
                options={THEME_OPTIONS}
                onChange={(v) => updateSetting('theme', v as AppSettings['theme'])}
                label="Theme"
              />
              <ToggleSwitch
                enabled={settings.compactMode}
                onChange={(v) => updateSetting('compactMode', v)}
                label="Compact Mode"
                description="Reduce spacing and font sizes"
              />
            </SettingsCard>

            {/* Data Preferences */}
            <SettingsCard>
              <SectionHeader icon={Database} title="Data Preferences" description="Configure defaults" />
              <SelectOption
                value={settings.defaultCountry}
                options={COUNTRY_OPTIONS.map(c => ({ value: c.code, label: c.name }))}
                onChange={(v) => updateSetting('defaultCountry', v)}
                label="Default Country"
              />
              <SelectOption
                value={settings.defaultTimeRange}
                options={TIME_RANGE_OPTIONS}
                onChange={(v) => updateSetting('defaultTimeRange', v as AppSettings['defaultTimeRange'])}
                label="Time Range"
              />
            </SettingsCard>
          </div>

          {/* Row 2: Refresh + Sources */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Data Refresh */}
            <SettingsCard>
              <SectionHeader icon={RefreshCw} title="Data Refresh" description="Auto-update settings" />
              <ToggleSwitch
                enabled={settings.autoRefresh}
                onChange={(v) => updateSetting('autoRefresh', v)}
                label="Auto-refresh"
                description="Refresh data periodically"
              />
              {settings.autoRefresh && (
                <SelectOption
                  value={settings.refreshInterval.toString()}
                  options={REFRESH_INTERVAL_OPTIONS.map(o => ({ value: o.value.toString(), label: o.label }))}
                  onChange={(v) => updateSetting('refreshInterval', parseInt(v))}
                  label="Refresh Interval"
                />
              )}
            </SettingsCard>

            {/* Data Sources */}
            <SettingsCard>
              <SectionHeader icon={Globe} title="Data Sources" description="Enable/disable sources" />
              <ToggleSwitch
                enabled={settings.enableIMF}
                onChange={(v) => updateSetting('enableIMF', v)}
                label="IMF Data"
              />
              <ToggleSwitch
                enabled={settings.enableWorldBank}
                onChange={(v) => updateSetting('enableWorldBank', v)}
                label="World Bank Data"
              />
              <ToggleSwitch
                enabled={settings.enableStaticFallback}
                onChange={(v) => updateSetting('enableStaticFallback', v)}
                label="Static Fallback"
              />
            </SettingsCard>
          </div>

          {/* Row 3: Notifications + Export */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Notifications */}
            <SettingsCard>
              <SectionHeader icon={Bell} title="Notifications" description="Alert settings" />
              <ToggleSwitch
                enabled={settings.enableNotifications}
                onChange={(v) => updateSetting('enableNotifications', v)}
                label="Enable Notifications"
              />
              {settings.enableNotifications && (
                <div className="py-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-white">Alert Threshold</span>
                    <span className="text-xs text-data-amber">{settings.alertThreshold}%</span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={20}
                    value={settings.alertThreshold}
                    onChange={(e) => updateSetting('alertThreshold', parseInt(e.target.value))}
                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
              )}
            </SettingsCard>

            {/* Export & Data */}
            <SettingsCard>
              <SectionHeader icon={Download} title="Export & Data" description="Export settings" />
              <SelectOption
                value={settings.defaultExportFormat}
                options={EXPORT_FORMAT_OPTIONS}
                onChange={(v) => updateSetting('defaultExportFormat', v as AppSettings['defaultExportFormat'])}
                label="Export Format"
              />
              <div className="py-3">
                <span className="text-sm text-white block mb-2">Cache</span>
                {showClearCacheConfirm ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Clear cache?</span>
                    <button onClick={handleClearCache} className="px-2 py-1 text-xs bg-data-red/20 text-data-red rounded">Confirm</button>
                    <button onClick={() => setShowClearCacheConfirm(false)} className="px-2 py-1 text-xs text-gray-400">Cancel</button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowClearCacheConfirm(true)}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs bg-terminal-bg border border-terminal-border rounded text-gray-400 hover:text-data-red"
                  >
                    <Trash2 size={12} />
                    Clear Cache
                  </button>
                )}
              </div>
            </SettingsCard>
          </div>

          {/* Row 4: About + Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* About */}
            <SettingsCard>
              <SectionHeader icon={Info} title="About" description="Application info" />
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-terminal-border/50">
                  <span className="text-gray-500">Application</span>
                  <span className="text-white">Silk Road Terminal 2.0</span>
                </div>
                <div className="flex justify-between py-2 border-b border-terminal-border/50">
                  <span className="text-gray-500">Version</span>
                  <span className="text-white">0.1.0</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-500">Data Sources</span>
                  <span className="text-white">IMF, World Bank, CBU</span>
                </div>
                <a href="https://natex.uz" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-data-amber hover:underline mt-2">
                  <ExternalLink size={10} />
                  Visit NATEX Website
                </a>
              </div>
            </SettingsCard>

            {/* Actions */}
            <SettingsCard>
              <SectionHeader icon={Settings} title="Settings Management" description="Import/export/reset" />
              <div className="flex flex-wrap gap-2 mb-3">
                <button onClick={handleExport} className="flex items-center gap-2 px-3 py-2 text-xs bg-terminal-bg border border-terminal-border rounded text-gray-300 hover:text-white">
                  <Download size={12} />
                  Export
                </button>
                <button onClick={handleImport} className="flex items-center gap-2 px-3 py-2 text-xs bg-terminal-bg border border-terminal-border rounded text-gray-300 hover:text-white">
                  <Upload size={12} />
                  Import
                </button>
                <button onClick={handleCopySettings} className="flex items-center gap-2 px-3 py-2 text-xs bg-terminal-bg border border-terminal-border rounded text-gray-300 hover:text-white">
                  <Copy size={12} />
                  Copy
                </button>
              </div>
              <div className="pt-2 border-t border-terminal-border/50">
                {showResetConfirm ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Reset all?</span>
                    <button onClick={handleReset} className="px-2 py-1 text-xs bg-data-red/20 text-data-red rounded">Confirm</button>
                    <button onClick={() => setShowResetConfirm(false)} className="px-2 py-1 text-xs text-gray-400">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => setShowResetConfirm(true)} className="flex items-center gap-2 px-3 py-2 text-xs bg-data-red/10 border border-data-red/30 rounded text-data-red">
                    <RotateCcw size={12} />
                    Reset to Defaults
                  </button>
                )}
              </div>
            </SettingsCard>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="h-6 bg-terminal-header border-t border-terminal-border flex items-center px-4 text-[9px] text-gray-500 shrink-0">
        <span>Settings saved automatically to local storage</span>
        <div className="flex-1" />
        <span>Silk Road Terminal v0.1.0</span>
      </div>
    </div>
  );
};

export default SettingsView;
